import React, { useState } from 'react';

import type { CompanyInfoDTO } from '../../services/apiClient';

import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import handleChange from '../../utils/formUtils/handleChange';

interface CompanyInfoFormProps {
  initialValues: CompanyInfoDTO;
  onSubmit: (data: CompanyInfoDTO) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const FORM_SECTIONS = {
  companyDetails: {
    id: 'company-details',
    title: 'Company Details',
  },
  invoiceInfo: {
    id: 'invoice-info',
    title: 'Invoice Information',
  },
  additionalInfo: {
    id: 'additional-info',
    title: 'Additional Information',
  },
} as const;

const CompanyInfoForm: React.FC<CompanyInfoFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<CompanyInfoDTO>(initialValues);

  const handleSave = () => {
    onSubmit(formData);
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
    >
      <FormSection
        id={FORM_SECTIONS.companyDetails.id}
        title={FORM_SECTIONS.companyDetails.title}
      >
        <FormField
          id="name"
          label="Name"
          type="text"
          value={formData.name || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter name"
        />
        <FormField
          id="email"
          label="Email"
          type="email"
          value={formData.email || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter email"
          textCase='lowercase'
        />
        <FormField
          id="phone"
          label="Phone"
          type="text"
          value={formData.phone || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter phone number"
        />
        <FormField
          id="address"
          label="Address"
          type="text"
          value={formData.address || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter address"
        />
        <FormField
          id="pbo"
          label="PBO"
          type="text"
          value={formData.pbo || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter PBO"
        />
        <FormField
          id="npc"
          label="NPC"
          type="text"
          value={formData.npc || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter NPC"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.invoiceInfo.id}
        title={FORM_SECTIONS.invoiceInfo.title}
      >
        <FormField
          id="bank"
          label="Bank"
          type="text"
          value={formData.bank || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter bank"
        />
        <FormField
          id="accountType"
          label="Account Type"
          type="text"
          value={formData.accountType || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter account type"
        />
        <FormField
          id="branchCode"
          label="Branch Code"
          type="text"
          value={formData.branchCode || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter branch code"
        />
        <FormField
          id="accountNumber"
          label="Account Number"
          type="text"
          value={formData.accountNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter account number"
        />
        <FormField
          id="swiftCode"
          label="Swift Code"
          type="text"
          value={formData.swiftCode || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter swift code"
        />
        <FormField
          id="vatNumber"
          label="VAT Number"
          type="text"
          value={formData.vatNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter VAT number"
        />
         <FormField
          id="lastUsedInvoiceNumber"
          label="Last Used Invoice Number"
          type="number"
          value={formData.lastUsedInvoiceNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter last used invoice number"
        />

         <FormField
          id="lastUsedPaymentNoticeNumber"
          label="Last Used Payment Notice Number"
          type="number"
          value={formData.lastUsedPaymentNoticeNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter last used payment notice number"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.additionalInfo.id}
        title={FORM_SECTIONS.additionalInfo.title}
        columns={1}
      >
        <FormField
          id="webAddress"
          label="Web Address"
          type="text"
          value={formData.webAddress || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter web address"
        />
        <FormField
          id="notes"
          label="Notes"
          type="textarea"
          value={formData.notes || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter notes"
        />
      </FormSection>
    </Form>
  );
};

export default CompanyInfoForm;
